import { getFormProps, getInputProps, useForm } from "@conform-to/react";
import { getZodConstraint, parseWithZod } from "@conform-to/zod";
import type { ActionFunctionArgs, MetaFunction } from "@remix-run/node";
import { json, redirect } from "@remix-run/node";
import { Form, Link, useActionData } from "@remix-run/react";
import { AuthenticityTokenInput } from "remix-utils/csrf/react";
import { HoneypotInputs } from "remix-utils/honeypot/react";
import { z } from "zod";

import { publishSignup } from "./register.server";
import { prepareVerification } from "./verify.server";
import { GeneralErrorBoundary } from "~/components/error-boundary";
import { ErrorList, Field } from "~/components/forms";
import { SlimLayout } from "~/components/marketing/lib/SlimLayout";
import Logo from "~/components/ui/logo";
import { StatusButton } from "~/components/ui/status-button";
import { validateCSRF } from "~/lib/csrf.server";
import { checkHoneypot } from "~/lib/honeypot.server";
import { useIsPending } from "~/lib/misc";
import { EmailSchema } from "~/lib/user-validation";
import { AccountsService } from "~/models/accounts/accountsService.server";

const SignupSchema = z.object({
  email: EmailSchema,
});

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();

  await validateCSRF(formData, request.headers);
  checkHoneypot(formData);

  const submission = await parseWithZod(formData, {
    schema: SignupSchema.superRefine(async (data, ctx) => {
      const existingUser = await AccountsService.entities.users.query
        .user_email({
          email: data.email,
        })
        .go();
      if (existingUser.data.length) {
        ctx.addIssue({
          path: ["email"],
          code: z.ZodIssueCode.custom,
          message: "A user already exists with this email",
        });

        return;
      }
    }),
    async: true,
  });

  if (submission.status !== "success") {
    return json(
      { result: submission.reply() },
      { status: submission.status === "error" ? 400 : 200 },
    );
  }

  const { email } = submission.value;
  const { verifyUrl, redirectTo, otp } = await prepareVerification({
    period: 10 * 60,
    request,
    type: "onboarding",
    target: email,
  });

  try {
    await publishSignup({ email, verifyUrl: verifyUrl.toString(), otp });

    return redirect(redirectTo.toString());
  } catch (e) {
    console.error("Event publish error", e);

    return json(
      {
        result: submission.reply({
          formErrors: ["Unable to send registration email"],
        }),
      },
      { status: 500 },
    );
  }
}

export const meta: MetaFunction = () => {
  return [{ title: "Sign Up | CandidateStream" }];
};

export default function Register() {
  const actionData = useActionData<typeof action>();
  const isPending = useIsPending();
  // const [searchParams] = useSearchParams();
  // const redirectTo = searchParams.get("redirectTo");

  const [form, fields] = useForm({
    id: "signup-form",
    constraint: getZodConstraint(SignupSchema),
    lastResult: actionData?.result,
    onValidate({ formData }) {
      const result = parseWithZod(formData, { schema: SignupSchema });

      return result;
    },
    shouldRevalidate: "onBlur",
  });

  return (
    <SlimLayout>
      <div className="flex">
        <Link to="/" aria-label="Home" className="w-full">
          <div className="flex justify-center">
            <Logo
              variant="mark-word"
              className="w-auto text-card-foreground h-12"
            />
          </div>
        </Link>
      </div>
      <h2 className="mt-20 text-lg font-semibold text-gray-900">
        Get started for free
      </h2>
      <p className="mt-2 text-sm text-gray-700">
        Already registered?{" "}
        <Link
          to="/auth/login"
          className="font-medium text-blue-600 hover:underline"
        >
          Sign in
        </Link>{" "}
        to your account.
      </p>
      <Form
        method="POST"
        {...getFormProps(form)}
        className="mt-10 grid grid-cols-1 gap-y-8"
      >
        <AuthenticityTokenInput />
        <HoneypotInputs />
        <Field
          labelProps={{
            htmlFor: fields.email.id,
            children: "Email",
          }}
          inputProps={{
            ...getInputProps(fields.email, { type: "email" }),
            autoFocus: true,
          }}
          errors={fields.email.errors}
        />
        <ErrorList errors={form.errors} id={form.errorId} />
        <div className="flex items-center justify-between gap-6 pt-3">
          <StatusButton
            className="w-full"
            status={isPending ? "pending" : form.status ?? "idle"}
            type="submit"
            disabled={isPending}
          >
            Submit
          </StatusButton>
        </div>
      </Form>
    </SlimLayout>
  );
}

export function ErrorBoundary() {
  return <GeneralErrorBoundary />;
}
